import type { PositionedNode } from './chartLayout'
import { resolveDrop } from './dropTargets'
import type { DropHint, DropLabels } from './dropTargets'
import { descendantIds, siblingsOf } from './seatMoves'
import type { SeatDetail } from './types'

/**
 * What releasing a connector arrow's child-end endpoint means.
 *
 * An arrow IS a reporting line, so every release resolves to exactly one of
 * four answers, and each of them has to be visible to the user:
 *   - `reparent` — dropped on another box: that box becomes the parent
 *   - `detach`   — dropped in open space: the box becomes a top-level box
 *   - `refused`  — a shape the chart cannot take (a cycle, itself, no rights)
 *   - `noop`     — dropped back on the box it already reports to
 *
 * The hit test is the pointer alone. Unlike a card drag there is nothing being
 * carried, so there is no card rectangle to fall back on: the endpoint is
 * exactly where the pointer is.
 */
export type ConnectorOutcome = 'reparent' | 'detach' | 'refused' | 'noop' | 'cancel'

export interface ConnectorResolution {
  outcome: ConnectorOutcome
  /** Box the live highlight is painted on, if the pointer is over one. */
  anchorSeatId: number | null
  hint: DropHint | null
  /** The new parent, set only for an accepted re-parent. */
  parentSeatId: number | null
  /** Index among the destination's children, set for reparent and detach. */
  position: number
  /** Why a release was refused, or why it changed nothing. */
  reason?: string
}

export interface ResolveConnectorInput {
  /** Laid-out visible cards, in flow coordinates. */
  nodes: PositionedNode[]
  /** Every seat in the chart (with any optimistic move already applied). */
  seats: SeatDetail[]
  /** The box the dragged endpoint belongs to — the arrow's child. */
  childSeatId: number
  /** The endpoint, in flow coordinates. */
  pointer: { x: number; y: number }
  labels: DropLabels
}

const inside = (node: PositionedNode, point: { x: number; y: number }) =>
  point.x >= node.x && point.x <= node.x + node.width &&
  point.y >= node.y && point.y <= node.y + node.height

/** Where the arrow would land, and what that would do to the chart. */
export function resolveConnectorEndpoint(input: ResolveConnectorInput): ConnectorResolution {
  const { nodes, seats, childSeatId, pointer, labels } = input
  const child = seats.find(s => s.id === childSeatId)

  const detach: ConnectorResolution = {
    outcome: 'detach',
    anchorSeatId: null,
    hint: null,
    parentSeatId: null,
    position: siblingsOf(seats, null).filter(s => s.id !== childSeatId).length,
  }

  if (!child) return detach

  const over = nodes.find(node => inside(node, pointer))
  if (!over) return detach

  if (over.seat.id === childSeatId) {
    return {
      outcome: 'refused',
      anchorSeatId: over.seat.id,
      hint: 'invalid',
      parentSeatId: null,
      position: 0,
      reason: `A ${labels.seat} can't report to itself.`,
    }
  }

  // A cycle and a missing permission are refused the same way a dragged card
  // is, so an arrow and a card never disagree about what the chart allows.
  if (descendantIds(seats, childSeatId).has(over.seat.id)) {
    return {
      outcome: 'refused',
      anchorSeatId: over.seat.id,
      hint: 'invalid',
      parentSeatId: null,
      position: 0,
      reason: `"${over.seat.name}" reports to "${child.name}", so it can't become its parent.`,
    }
  }

  const resolution = resolveDrop({
    nodes,
    seats,
    draggedSeatId: childSeatId,
    // The endpoint has no rectangle: pointer and "card" are the same point.
    card: { x: pointer.x, y: pointer.y, width: 0, height: 0 },
    pointer,
    labels,
  })
  if (!resolution) return detach

  if (resolution.hint === 'invalid' || resolution.parentSeatId === null) {
    return {
      outcome: 'refused',
      anchorSeatId: resolution.anchorSeatId,
      hint: 'invalid',
      parentSeatId: null,
      position: 0,
      reason: resolution.reason || `You can't point this arrow at "${over.seat.name}".`,
    }
  }

  if (resolution.parentSeatId === child.parent_seat_id) {
    return {
      outcome: 'noop',
      anchorSeatId: resolution.anchorSeatId,
      hint: 'child',
      parentSeatId: resolution.parentSeatId,
      position: resolution.position,
      reason: `"${child.name}" already reports to "${over.seat.name}", so nothing changed.`,
    }
  }

  return {
    outcome: 'reparent',
    anchorSeatId: resolution.anchorSeatId,
    hint: 'child',
    parentSeatId: resolution.parentSeatId,
    position: resolution.position,
  }
}

/** A new line has no loose existing end to detach: open space just cancels. */
export interface NewConnectorResolution extends ConnectorResolution {
  childSeatId: number | null
}

export function resolveNewConnectorEndpoint(
  input: Omit<ResolveConnectorInput, 'childSeatId'> & { originSeatId: number; direction: 'top' | 'bottom' },
): NewConnectorResolution {
  const { nodes, seats, originSeatId, direction, pointer, labels } = input
  const origin = seats.find(s => s.id === originSeatId)
  const over = nodes.find(node => inside(node, pointer))?.seat
  const cancel: NewConnectorResolution = {
    outcome: 'cancel', anchorSeatId: null, hint: null, parentSeatId: null, position: 0, childSeatId: null,
  }
  if (!origin || !over) return cancel

  const child = direction === 'top' ? origin : over
  const parent = direction === 'top' ? over : origin
  const refused = (reason: string): NewConnectorResolution => ({
    outcome: 'refused', anchorSeatId: over.id, hint: 'invalid',
    parentSeatId: null, position: 0, childSeatId: child.id, reason,
  })

  if (child.id === parent.id) return refused(`A ${labels.seat} can't report to itself.`)
  if (descendantIds(seats, child.id).has(parent.id)) {
    return refused(`"${parent.name}" reports to "${child.name}", so it can't become its parent.`)
  }
  if (!child.can_edit) return refused(`You don't have permission to change where "${child.name}" reports.`)
  if (!parent.can_edit) return refused(`You don't have permission to add ${labels.seats} under "${parent.name}".`)

  const position = siblingsOf(seats, parent.id).filter(s => s.id !== child.id).length
  if (child.parent_seat_id === parent.id) {
    return {
      outcome: 'noop', anchorSeatId: over.id, hint: 'child', parentSeatId: parent.id,
      position, childSeatId: child.id,
      reason: `"${child.name}" already reports to "${parent.name}", so nothing changed.`,
    }
  }
  return {
    outcome: 'reparent', anchorSeatId: over.id, hint: 'child',
    parentSeatId: parent.id, position, childSeatId: child.id,
  }
}

/** Boxes that travel with a detached box, including the box itself. */
export function branchSize(seats: SeatDetail[], seatId: number): number {
  return descendantIds(seats, seatId).size
}
